import { getQuestionnaireAnswers } from '@medplum/core'
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemInitial,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes'
import { maybeGetReferenceString } from '#src/references.ts'
import { QUESTIONNAIRE_HIDDEN_EXTENSION_URL } from '#src/structureDefinitions.ts'
import { generateUUID } from '#src/utils.ts'

export function tryGetQuestionnaireAnswers(
  questionnaireResponse: QuestionnaireResponse
): Record<string, QuestionnaireResponseItemAnswer> | undefined {
  try {
    return getQuestionnaireAnswers(questionnaireResponse)
  } catch (_e) {
    return undefined
  }
}

// identical to medplum's buildInitialResponse
export function buildInitialResponse(
  questionnaire: Questionnaire
): Pick<
  QuestionnaireResponse,
  'resourceType' | 'questionnaire' | 'item' | 'status'
> {
  const response: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    questionnaire: maybeGetReferenceString(questionnaire),
    item: buildInitialResponseItems(questionnaire.item),
    status: 'completed',
  }
  return response
}

/** different from medplum's buildInitialResponseItems because we filter out invalid items */
export function buildInitialResponseItems(
  items: QuestionnaireItem[] | undefined
): QuestionnaireResponseItem[] {
  return items?.filter(isValidResponseItem).map(buildInitialResponseItem) ?? []
}

function isValidResponseItem(item: QuestionnaireItem): boolean {
  if (item.type === 'display' || item.readOnly) {
    return false
  }
  if (
    item.extension?.some(
      (extension) => extension.url === QUESTIONNAIRE_HIDDEN_EXTENSION_URL
    )
  ) {
    return false
  }
  return true
}

// identical to medplum's buildInitialResponseItem
export function buildInitialResponseItem(
  item: QuestionnaireItem
): QuestionnaireResponseItem {
  return {
    id: generateUUID(),
    linkId: item.linkId,
    text: item.text,
    item: buildInitialResponseItems(item.item),
    answer: item.initial?.map(buildInitialResponseAnswer) ?? [],
  }
}

// identical to medplum's buildInitialResponseAnswer
function buildInitialResponseAnswer(
  answer: QuestionnaireItemInitial
): QuestionnaireResponseItemAnswer {
  // This works because QuestionnaireItemInitial and QuestionnaireResponseItemAnswer
  // have the same properties.
  return { ...answer }
}

export function extractQuestionnaireItemsRecursive(
  items: QuestionnaireItem[]
): QuestionnaireItem[] {
  return items.flatMap((item) => [
    ...(isValidFormItem(item) ? [item] : []),
    ...(isIterableItem(item) && item.item
      ? extractQuestionnaireItemsRecursive(item.item)
      : []),
  ])
}

function isValidFormItem(item: QuestionnaireItem): boolean {
  if (
    item.type === 'display' ||
    item.type === 'group' ||
    item.type === 'question'
  ) {
    return false
  }
  if (item.readOnly) {
    return false
  }
  if (
    item.extension?.some(
      (extension) => extension.url === QUESTIONNAIRE_HIDDEN_EXTENSION_URL
    )
  ) {
    return false
  }
  return true
}

function isIterableItem(item: QuestionnaireItem): boolean {
  if (item.readOnly) {
    return false
  }
  if (
    item.extension?.some(
      (extension) => extension.url === QUESTIONNAIRE_HIDDEN_EXTENSION_URL
    )
  ) {
    return false
  }
  return true
}
